import React from 'react';
import { Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { BoardKpiVo } from 'types/insuranceBilling';
import { formatMoney } from './statusConfig';
import s from './s.module.less';

// 全时段（不带日期）：10-20 数字核对文档 D1 产品要求加的 Total 维度，放在下拉第一个
export const KPI_PERIOD_ALL_TIME = 0;
// 时间筛选的选项：All time 在前，后接与 Financials 期间下拉同一套的 Last 1 到 6 months；默认 All time
export const KPI_PERIOD_OPTIONS = [
    { value: KPI_PERIOD_ALL_TIME, label: 'All time' },
    ...[1, 2, 3, 4, 5, 6].map((m) => ({
        value: m,
        label: `Last ${m} month${m > 1 ? 's' : ''}`,
    })),
];
export const DEFAULT_KPI_PERIOD = KPI_PERIOD_ALL_TIME;

type Props = {
    // summary.kpi；null（接口失败 / 未返回）时数字显示 —
    kpi: BoardKpiVo | null;
    loading?: boolean;
    // 选中的月数（KPI_PERIOD_ALL_TIME = 全时段）；按 date of service 筛，整页（卡片 + 表格）共用
    period: number;
    onPeriodChange: (months: number) => void;
    // waiting 态（还没有 claim）三张卡都是 0，切期间没有意义，不显示下拉
    showPeriod?: boolean;
};

// KPI 卡（原型 kpis()）：count + $ 金额 + info tooltip；waiting 态展示 0 / $0.00。
// 09/09 起原来的 Needs Attention 卡挪去了表格上方的 NEEDS ATTENTION 区（按 issue 类型拆开、可点击筛表）；
// 10-21 起数据改读 summary.kpi，加回第四张 Needs attention / Other（口径是「其余所有 claim」，与上方区域不同）。
// 10/05 起卡片上方加了时间筛选（原型 kpiPeriodHtml），tooltip 文案随之改成 "in the selected period"。
const KpiCards = ({ kpi, loading, period, onPeriodChange, showPeriod = true }: Props) => {
    // 10-21 D3/D4：四张卡按 tab 分组，每条 claim 只落一张，加起来等于 Total。
    // Paid 卡名字与金额口径（实收）不变，数据取 kpi.completed（Completed tab）；实收下面另起一行
    // 「of $X billed」，让四张卡的收费也能对上 Total（其余三张卡的金额本来就是收费）
    const cards: { label: string; count?: number; amt?: number | null; billedOf?: number; tip: string }[] = [
        {
            label: 'Total Claims',
            count: kpi?.total.count,
            amt: kpi?.total.billed,
            tip: 'Every claim Klarity is handling in this view with a date of service in the selected period, across all statuses. The amount is the total billed to payers.',
        },
        {
            label: 'Submitted',
            count: kpi?.submitted.count,
            amt: kpi?.submitted.billed,
            tip: 'Claims in the selected period submitted to the clearinghouse or the payer and awaiting a decision. The amount is the total billed.',
        },
        {
            label: 'Paid',
            count: kpi?.completed.count,
            amt: kpi?.completed.paid,
            billedOf: kpi?.completed.billed,
            tip: 'Claims in the selected period the payer has paid, in full or in part. The amount is the total collected so far.',
        },
        {
            label: 'Needs attention / Other',
            count: kpi?.other.count,
            amt: kpi?.other.billed,
            tip: 'All other claims in the selected period: denied, rejected, still in preparation, cancelled, or closed. The amount is the total billed.',
        },
    ];

    return (
        <>
            {showPeriod && (
                <div className={s.kpiPeriod}>
                    <Select
                        value={period}
                        onChange={onPeriodChange}
                        options={KPI_PERIOD_OPTIONS}
                        className={s.kpiPeriodSelect}
                        dropdownStyle={{ minWidth: '160px' }}
                    />
                </div>
            )}
            <div className={s.kpis}>
                {cards.map((c) => (
                    <div key={c.label} className={s.kpi}>
                        <div className={s.kpiTop}>
                            <span className={s.kpiLabel}>{c.label}</span>
                            <Tooltip title={c.tip}>
                                <InfoCircleOutlined className={s.kpiInfo} />
                            </Tooltip>
                        </div>
                        <div className={s.kpiCount}>{loading || c.count == null ? '—' : c.count}</div>
                        <div className={s.kpiAmt}>
                            {loading ? '—' : formatMoney(c.amt)}
                            {!loading && c.billedOf != null && (
                                <span className={s.kpiAmtSub}>of {formatMoney(c.billedOf)} billed</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default KpiCards;
